import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import UserService from '../../services/User.service';
import type { Payment } from '../../types/user.types';
import type { RootState } from '../store';

interface PaymentsState {
  payments: Payment[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: PaymentsState = {
  payments: [],
  status: 'idle',
  error: null,
};

export const fetchPayments = createAsyncThunk<Payment[]>('payments/fetchPayments', async (_, { rejectWithValue }) => {
  try {
    const res = await UserService.getPayments();
    if (res.error) {
      return rejectWithValue(res.message || 'Failed to fetch payments.');
    }

    return (res.data || []) as Payment[];
  } catch (err: any) {
    return rejectWithValue(err.message || 'An unknown error occurred.');
  }
});

export const paymentsSlice = createSlice({
  name: 'payments',
  initialState,
  reducers: {
    setPayments(state, action: PayloadAction<Payment[]>) {
      state.payments = action.payload;
      state.status = 'succeeded';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPayments.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchPayments.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.payments = action.payload;
      })
      .addCase(fetchPayments.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      });
  },
});

export const { setPayments } = paymentsSlice.actions;

export const selectAllPayments = (state: RootState) => state.payments.payments;
export const selectPaymentsStatus = (state: RootState) => state.payments.status;
export const selectPaymentsError = (state: RootState) => state.payments.error;

// eslint-disable-next-line import/no-default-export
export default paymentsSlice.reducer;
